import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Smartphone, Palette, Puzzle, Shield, Bell, 
  Users, Calendar, CreditCard, Key, MessageSquare, BarChart3,
  Zap, Globe, Star, Download
} from 'lucide-react';

function MobileAppPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroFeatures = [
    "100% Personalizable con tu Marca",
    "Módulos como Piezas de Rompecabezas",
    "iOS y Android Nativo",
    "Actualización Instantánea"
  ];

  const appModules = [
    {
      icon: Users,
      title: "Gestión de Clientes",
      description: "Acceso completo al CRM desde el móvil. Consulta información, historial y gestiona clientes en tiempo real.",
      color: "blue"
    },
    {
      icon: Calendar,
      title: "Reservas y Agenda",
      description: "Sistema de reservas integrado. Tus clientes pueden reservar espacios, servicios o citas directamente desde la app.",
      color: "green"
    },
    {
      icon: Key,
      title: "Control de Acceso",
      description: "Acceso sin contacto mediante QR dinámico. Gestión de visitantes y credenciales digitales.",
      color: "purple"
    },
    {
      icon: CreditCard,
      title: "Pagos y Facturación",
      description: "Consulta de facturas, pagos pendientes y estados de cuenta. Notificaciones automáticas de vencimientos.",
      color: "red"
    },
    {
      icon: MessageSquare,
      title: "Comunicación Directa",
      description: "Chat integrado, notificaciones push y sistema de tickets de soporte. Mantén comunicación fluida con tus clientes.",
      color: "indigo"
    },
    {
      icon: BarChart3,
      title: "Dashboard Personalizado",
      description: "Visualiza métricas clave, reportes y estadísticas. Interfaz adaptada a cada tipo de usuario.",
      color: "orange"
    }
  ];

  const brandingFeatures = [
    {
      icon: Palette,
      title: "Tu Marca, Tu App",
      description: "Logo, colores corporativos, tipografía y diseño 100% personalizado para reflejar tu identidad de marca."
    },
    {
      icon: Puzzle,
      title: "Módulos Modulares",
      description: "Selecciona solo los módulos que necesitas. Como un rompecabezas, cada pieza encaja perfectamente con las demás."
    },
    {
      icon: Globe,
      title: "Multi-idioma",
      description: "Soporte para múltiples idiomas. Ideal para empresas con operaciones internacionales."
    },
    {
      icon: Shield,
      title: "Seguridad Avanzada",
      description: "Autenticación biométrica, encriptación de datos y cumplimiento con estándares de seguridad internacionales."
    }
  ];

  const benefits = [
    {
      stat: "10x",
      label: "Más Engagement",
      description: "Los usuarios interactúan 10 veces más con una app nativa vs web móvil"
    },
    {
      stat: "60%",
      label: "Menos Tickets",
      description: "Reduce consultas de soporte con información self-service en la app"
    },
    {
      stat: "3 Días",
      label: "Lanzamiento",
      description: "Tu app personalizada lista para publicar en 3 días hábiles"
    },
    {
      stat: "100%",
      label: "Nativa",
      description: "Aplicación nativa para iOS y Android con máximo rendimiento"
    }
  ];

  const useCases = [
    {
      title: "Coworking Spaces",
      description: "Reserva de espacios, control de acceso QR, pagos de membresías y comunicación con comunidad.",
      icon: "🏢"
    },
    {
      title: "Gimnasios",
      description: "Check-in digital, reserva de clases, tracking de progreso y renovación de membresías.",
      icon: "💪"
    },
    {
      title: "Inmobiliarias",
      description: "Catálogo de propiedades, tours virtuales, agenda de visitas y seguimiento de interesados.",
      icon: "🏘️"
    },
    {
      title: "Edificios Multifamily",
      description: "Pago de gastos comunes, reserva de amenities, reportes de problemas y comunicados.",
      icon: "🏗️"
    },
    {
      title: "Empresas B2B",
      description: "Portal de clientes, consulta de servicios, facturación y soporte técnico integrado.",
      icon: "💼"
    },
    {
      title: "Retail & Servicios",
      description: "Catálogo de productos, programa de fidelización, agendamiento y pagos en línea.",
      icon: "🛍️"
    }
  ];

  return (
    <>
      <SEOHead
        title="App Móvil Personalizada - Cloudbase"
        description="Aplicación móvil nativa con tu marca. Módulos modulares como un rompecabezas. iOS y Android. Lista en 3 días."
        keywords="app personalizada, aplicacion movil, white label app, app nativa, ios android"
        path="/app-personalizada"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Productos', item: '/#modulos' },
          { name: 'App Personalizada', item: '/app-personalizada' }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                App Móvil Nativa
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Tu App con Tu Marca
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Aplicación móvil 100% personalizada con tu logo, colores y funcionalidades. Los módulos funcionan como piezas de un rompecabezas que encajan perfectamente.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo de la App
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Solicitar Cotización
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Puzzle Concept Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-block mb-4">
                  <Puzzle className="w-16 h-16 text-green-600 mx-auto" />
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Módulos como Piezas de Rompecabezas
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Cada funcionalidad es un módulo independiente que se integra perfectamente con los demás. Activa solo lo que necesitas y expande cuando tu negocio crezca.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {appModules.map((module, index) => {
                  const IconComponent = module.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow border-l-4 border-green-600"
                    >
                      <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                        <IconComponent className="w-7 h-7 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {module.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {module.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Branding Features */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Personalización Total
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  La app lleva tu marca en cada detalle. No es nuestra app, es TU app.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {brandingFeatures.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div 
                      key={index}
                      className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-6">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-3">
                            {feature.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-br from-green-600 to-green-700">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                {benefits.map((benefit, index) => (
                  <div key={index} className="text-white">
                    <div className="text-5xl font-bold mb-3">{benefit.stat}</div>
                    <div className="text-xl font-semibold mb-2">{benefit.label}</div>
                    <p className="text-green-100 text-sm">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Apps para Cada Industria
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  La misma plataforma, infinitas posibilidades según tu industria
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {useCases.map((useCase, index) => (
                  <div 
                    key={index}
                    className="bg-gray-50 p-8 rounded-lg hover:shadow-lg transition-shadow text-center"
                  >
                    <div className="text-5xl mb-4">{useCase.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {useCase.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {useCase.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technical Features */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                  Características Técnicas
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Nativa iOS y Android</h4>
                    <p className="text-gray-600">Rendimiento óptimo en ambas plataformas</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Notificaciones Push</h4>
                    <p className="text-gray-600">Mantén a tus usuarios informados en tiempo real</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Modo Offline</h4>
                    <p className="text-gray-600">Funcionalidad básica sin conexión a internet</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Actualizaciones OTA</h4>
                    <p className="text-gray-600">Updates instantáneos sin pasar por stores</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Biometría Integrada</h4>
                    <p className="text-gray-600">Face ID, Touch ID y huella digital</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Deep Linking</h4>
                    <p className="text-gray-600">Enlaces directos a secciones específicas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <Smartphone className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Lanza Tu App en 3 Días
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                Solicita una demo personalizada y descubre cómo tu app puede estar lista para tus usuarios en tiempo récord
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo de la App
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold"
                  onClick={() => window.location.href = '/demo'}
                >
                  Descargar Caso de Éxito
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default MobileAppPage;
